import baseAPI from '@/api/base';
import { EditTicket, GetTicket, RegisterTicket } from '../dtos/ticket.dto';
import { DELETE_TICKET_URL, TICKET_URL, EDIT_TICKET_URL, ADD_TICKET_URL, CLOSE_TICKET_URL, TICKET_UNREAD_URL } from '@/utils/urls';
import { ticketFormData, TICKET_REQUEST_PARAMS, UPLOAD_REQUEST_CONFIG } from '@/utils/ticket';
import { AxiosResponse } from 'axios';

// Every request says who is asking (utils/ticket TICKET_REQUEST_PARAMS): in
// an organization's app, the organization — a Screenkode staff account
// included; in the admin, whoever is signed in. The same module lives in the
// admin and in both organization apps.
const params = (extra?: object) => ({ ...TICKET_REQUEST_PARAMS, ...(extra || {}) });

class Ticket {
    static getTicket = async(getTicket?: GetTicket) : Promise<AxiosResponse> => {
        return baseAPI.get(TICKET_URL, {
            params: params(getTicket)
        })
    }

    // A multipart form, so the files go with the request.
    static registerTicket = async(registerTicket: RegisterTicket) : Promise<AxiosResponse> => {
        const { attachments, ...fields } = registerTicket;
        return baseAPI.post(ADD_TICKET_URL, ticketFormData(fields, attachments), { ...UPLOAD_REQUEST_CONFIG, params: params() })
    }

    static editTicket = async(editTicket: EditTicket) : Promise<AxiosResponse> => {
        return baseAPI.put(EDIT_TICKET_URL, {
            ...editTicket
        }, { params: params() })
    }

    static closeTicket = async(editTicket: EditTicket) : Promise<AxiosResponse> => {
        return baseAPI.put(`${CLOSE_TICKET_URL}/${encodeURIComponent(editTicket.id)}`, {}, { params: params() })
    }

    static deleteTicket = async(ticketId: string) : Promise<AxiosResponse> => {
        return baseAPI.delete(`${DELETE_TICKET_URL}/${encodeURIComponent(ticketId)}`, { params: params() })
    }

    /** What the signed-in side has waiting across every ticket it can see. */
    static getUnread = async() : Promise<AxiosResponse> => {
        return baseAPI.get(TICKET_UNREAD_URL, { params: params() })
    }

    /** The signed-in side has the conversation open: everything sent so far is read. */
    static markRead = async(ticketId: string) : Promise<AxiosResponse> => {
        return baseAPI.put(`${TICKET_URL}/${encodeURIComponent(ticketId)}/read`, {}, { params: params() })
    }

    /** Everyone at Screenkode a ticket can be assigned to, with what each looks after. Staff only. */
    static getStaff = async() : Promise<AxiosResponse> => {
        return baseAPI.get(`${TICKET_URL}/staff`, { params: params() })
    }

    /** Gives a ticket to someone at Screenkode, or — with null — to nobody. Staff only. */
    static assign = async(ticketId: string, assigneeId: string | null) : Promise<AxiosResponse> => {
        return baseAPI.put(`${TICKET_URL}/${encodeURIComponent(ticketId)}/assignee`, { assigneeId }, { params: params() })
    }

    /** Moves a ticket along its lifecycle (utils/ticket_lifecycle): in_progress, resolved, closed or reopened. */
    static setStatus = async(ticketId: string, to: string, comment?: string) : Promise<AxiosResponse> => {
        return baseAPI.put(`${TICKET_URL}/${encodeURIComponent(ticketId)}/status`, comment ? { to, comment } : { to }, { params: params() })
    }
}

export default Ticket
